<template>
    <div>
        <header class="py-3">
            <b-container>
                <b-row class="align-items-center">
                    <b-col cols="12" class="text-center">
                        <router-link to="/"><img src="@/assets/thepaymentoption-logo-white.svg" class="logo"/></router-link>
                    </b-col>
                </b-row>
            </b-container>
        </header>

        <b-container class="mt-5 pb-5 mb-5">
            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
                <i class="fas fa-spinner fa-spin" style="font-size: 48px; color: #464577;"></i>
                <p class="mt-3 text-muted">Loading…</p>
            </div>

            <!-- Invalid / expired token -->
            <b-card v-else-if="error" class="text-center py-5">
                <i class="fas fa-exclamation-triangle text-warning mb-3" style="font-size: 64px;"></i>
                <h3 class="mb-3">{{ errorTitle }}</h3>
                <p class="text-muted">{{ errorMessage }}</p>
            </b-card>

            <!-- Uploaded — done -->
            <b-card v-else-if="uploaded" class="text-center py-5 success-card">
                <i class="fas fa-check-circle text-success mb-3" style="font-size: 80px;"></i>
                <h3 class="mb-3">{{ bypassed ? 'ID received' : 'All set!' }}</h3>
                <b-alert show variant="warning" class="text-left" v-if="bypassed">
                    We couldn't verify this photo automatically, so our team will review it manually.
                    An invalid or unreadable ID may delay or prevent approval.
                </b-alert>
                <p class="lead">Return to your computer to finish signing your agreement.</p>
                <p class="text-muted">You can close this page.</p>
            </b-card>

            <!-- Capture UI -->
            <div v-else>
                <b-alert show variant="danger" class="text-left" v-if="blockedMessages.length">
                    <strong>We couldn't accept that photo:</strong>
                    <ul class="mb-2 mt-2 pl-4">
                        <li v-for="msg in blockedMessages" :key="msg">{{ msg }}</li>
                    </ul>
                    <span>Please take a new photo of the front of your government-issued ID.</span>
                </b-alert>
                <b-card class="text-center capture-card">
                    <h3 class="mb-3" v-if="firstName">Hi {{ firstName }} — let's verify your ID</h3>
                    <h3 class="mb-3" v-else>Let's verify your ID</h3>
                    <p class="text-muted mb-3">
                        Take a clear photo of <strong>the front</strong> of your driver's license, state ID, or other government-issued photo ID — we only need the front side.
                        Place it on a flat surface in good lighting and make sure all four corners are visible.
                    </p>

                    <b-alert show variant="info" class="text-left rotate-tip mb-4">
                        <i class="fas fa-mobile-alt fa-rotate-90 mr-2"></i>
                        <strong>Turn your phone sideways</strong> before snapping the photo so the ID card fills the frame.
                    </b-alert>

                    <label class="btn btn-primary btn-lg btn-block capture-button" :class="{ disabled: uploading }">
                        <i class="fas fa-camera mr-2"></i>{{ uploading ? 'Uploading…' : 'Open camera' }}
                        <input type="file" accept="image/*" capture="environment" class="d-none" @change="onFileSelected" :disabled="uploading"/>
                    </label>

                    <label class="btn btn-outline-primary btn-block mt-3" :class="{ disabled: uploading }">
                        <i class="fas fa-image mr-2"></i>Choose from photos
                        <input type="file" accept="image/*" class="d-none" @change="onFileSelected" :disabled="uploading"/>
                    </label>

                    <p v-if="uploadError" class="text-danger mt-3 mb-0">{{ uploadError }}</p>
                </b-card>

                <p class="text-muted small text-center mt-3" v-if="expiresAt">
                    This link expires {{ expiresHumanized }}.
                </p>
            </div>
        </b-container>
    </div>
</template>

<script>
import {apiUrl} from '@/constants/config';
import axios from 'axios';
import { describeValidationIssues } from '@/utils/idValidationMessages';

export default {
    name: 'IdUpload',
    props: ['token'],
    data() {
        return {
            loading: true,
            error: null,
            errorTitle: '',
            errorMessage: '',
            firstName: '',
            expiresAt: null,
            uploaded: false,
            uploading: false,
            uploadError: '',
            blockedMessages: [],
            bypassed: false
        };
    },
    computed: {
        expiresHumanized() {
            if (!this.expiresAt) return '';
            try {
                const date = new Date(this.expiresAt);
                const mins = Math.max(0, Math.round((date.getTime() - Date.now()) / 60000));
                if (mins <= 0) return 'soon';
                if (mins < 60) return `in ${mins} minute${mins === 1 ? '' : 's'}`;
                return 'soon';
            } catch (e) { return ''; }
        }
    },
    mounted() {
        this.loadSession();
    },
    methods: {
        async loadSession() {
            this.loading = true;
            try {
                const res = await axios.get(`${apiUrl}api/account/id-upload/${this.token}`);
                this.firstName = res.data.firstName || '';
                this.expiresAt = res.data.expiresAt || null;
            } catch (err) {
                const code = (err && err.response && err.response.data && err.response.data.error) || '';
                this.error = code || 'unknown';
                if (code === 'token_expired') {
                    this.errorTitle = 'This link has expired';
                    this.errorMessage = 'Please return to your computer and request a new link.';
                } else if (code === 'token_consumed') {
                    this.errorTitle = 'This link has already been used';
                    this.errorMessage = 'Your ID was already uploaded. You can close this page.';
                } else if (code === 'already_onboarded') {
                    this.errorTitle = 'Agreement already completed';
                    this.errorMessage = 'This agreement has already been signed.';
                } else {
                    this.errorTitle = 'Link not found';
                    this.errorMessage = 'This link is invalid. Please return to your computer and request a new one.';
                }
            } finally {
                this.loading = false;
            }
        },
        async onFileSelected(event) {
            const file = event.target.files && event.target.files[0];
            event.target.value = '';
            if (!file) return;
            if (file.size > 10 * 1024 * 1024) {
                this.uploadError = 'That image is over 10 MB. Please pick a smaller photo.';
                return;
            }
            await this.uploadFile(file);
        },
        async uploadFile(file) {
            this.uploading = true;
            this.uploadError = '';
            try {
                const formData = new FormData();
                formData.append('image', file, file.name || 'id.jpg');
                const res = await axios.post(`${apiUrl}api/account/id-upload/${this.token}`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                });
                const gate = res.data && res.data.gate;
                if (gate && gate.blocked && !gate.bypassAllowed) {
                    // The AI check rejected the photo; the server kept this link
                    // alive so the customer can retake it right here.
                    const messages = describeValidationIssues(res.data.validation);
                    this.blockedMessages = messages.length ? messages : ['The photo couldn\'t be verified.'];
                    return;
                }
                this.blockedMessages = [];
                this.bypassed = !!(gate && gate.blocked && gate.bypassAllowed);
                this.uploaded = true;
            } catch (err) {
                const code = (err && err.response && err.response.data && err.response.data.error) || '';
                if (code === 'token_expired') {
                    this.uploadError = 'This link has expired. Please request a new one from your computer.';
                } else if (code === 'token_consumed') {
                    this.uploadError = 'This link was already used.';
                } else if (code === 'already_onboarded') {
                    this.uploadError = 'This agreement has already been signed.';
                } else if (code === 'unsupported_image_type') {
                    this.uploadError = 'That file type isn\'t supported. Use a JPG, PNG, or HEIC photo.';
                } else {
                    this.uploadError = 'Upload failed. Please try again.';
                }
            } finally {
                this.uploading = false;
            }
        }
    }
};
</script>

<style lang="scss" scoped>
header {
    background: #464577;
    .logo { height: 36px; }
}
.capture-card {
    border-left: 4px solid #464577;
}
.capture-button {
    padding: 18px;
    font-size: 1.1rem;
    cursor: pointer;
}
label.btn input[type="file"] {
    display: none;
}
label.btn.disabled {
    opacity: 0.6;
    pointer-events: none;
}
.success-card {
    border-left: 4px solid #28a745;
}
</style>
